/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactNode} from 'react';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {GenericObject} from '@ion/core/src/types/entities';
import {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';

export interface BaseDialogProps {
  children: ReactNode;
  actions?: ReactNode;
  show: boolean;
  title?: string;
  onClose?: (e: MouseEvent) => void;
  styleOverrides?: (theme: DLSTheme) => GenericObject;
}

const styles = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    maxWidth                       : '1067px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)'
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  titleContainer : {
    borderBottom : `solid 1px ${dlsColor('grey4')}`
  },
  titleContentContainer : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  titleTextContainer : {
    marginLeft : '20px'
  },
  contentContainer : {
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  formActions : {
    borderTop                      : 'solid 1px #BDBDBD',
    marginTop                      : 30,
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-between'
    },
    position   : 'sticky',
    bottom     : 0,
    background : '#F2F2F2'
  }
});

/**
 * Wrapper component for application dialogs
 *
 * ```typescript
 *
 * import {BaseDialog} from '@ion-bmw-portal/src/components/dialog';
 *
 * <BaseDialog show={show}
 *   title={title}
 *   onClose={onClose}>
 *   styleOverrides={styleOverrides}>
 *   {children}
 * </EditorDialog>
 *
 * ```
 *
 */
export const BaseDialog = ({
  children,
  actions,
  show,
  title,
  onClose,
  styleOverrides
}: BaseDialogProps) => {
  const dialogStyles         = useExtendStyles(styles, styleOverrides);
  const classes              = dialogStyles();
  const {isDesktop, isPhone} = useMedia();

  return (
    <Dialog fullScreen={!isDesktop}
      open={show}
      classes={getClasses(classes)}>
      {
        title &&
          <DialogTitle classes={{
            root : classes.titleContainer
          }}>
            <div className={classes.titleContentContainer}>
              <div onClick={onClose} className={classes.closeButton}>
                <MaterialExitCloseIcon size={IconSize.medium}
                  color={dlsColor('grey3')} />
              </div>
              <Typography className={isPhone ? classes.titleTextContainer : undefined} component='h1' variant='h2'>
                {title}
              </Typography>
            </div>
          </DialogTitle>
      }
      <DialogContent className={classes.contentContainer}>
        {children}
      </DialogContent>
      {Boolean(actions) && <DialogActions className={classes.formActions}>
        {actions}
      </DialogActions>}
    </Dialog>
  );
};

export default BaseDialog;
